/* An agent's Spaces tab: the second sidebar of its tables, then the open table (or the schema,
   or the first-visit page while it keeps none), and a row open beside it. Where it is — the
   table, the row, the schema — is the shell's, in the address (`/agents/{agent}/spaces/{table}/{row}`),
   so a notice's link opens the row it names. */
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { useSpaceTables } from "@/shared/api/spaces";

import RowPanel from "./RowPanel";
import SchemaView from "./SchemaView";
import SpacesRail from "./SpacesRail";
import TableChips from "./TableChips";
import StartPage from "./StartPage";
import { NewTableDialog, TableSettingsDialog } from "./SpaceDialogs";
import TableView from "./TableView";

import type { Agent, NavigationOptions, SpaceTable } from "@/shared/types";

/** The place in the address that is the schema, not a table. */
export const SCHEMA = "schema";

export interface SpacesPlace {
  /** A table's id, `schema`, or null for the first table there is. */
  tableId: string | null;
  /** A row of it, or `new` while one is being added. */
  rowId: string | null;
}

export default function SpacesPage({ agent, place, onPlace, onAskDefinable, onOpenRun }: {
  agent: Agent;
  place: SpacesPlace;
  onPlace: (place: SpacesPlace, how?: NavigationOptions) => void;
  onAskDefinable?: (text: string) => void;
  /** A row's activity names the run that wrote it: its trace. */
  onOpenRun?: (runId: string) => void;
}) {
  const { data: tables, isPending } = useSpaceTables(agent.id);
  const [dialog, setDialog] = useState<{ kind: "new" } | { kind: "settings"; table: SpaceTable } | null>(null);
  // A table just made: its id comes with the refetch, so it is opened by name once it lands.
  const [made, setMade] = useState<string | null>(null);
  useEffect(() => {
    const found = made && tables?.find((table) => table.name.toLowerCase() === made.toLowerCase());
    if (!found) return;
    setMade(null);
    onPlace({ tableId: found.id, rowId: null });
  }, [made, tables, onPlace]);

  if (isPending || !tables) {
    return (
      <div className="flex-1 grid place-items-center text-[var(--text-tertiary)]">
        <Loader2 aria-label="Loading tables" className="w-4 h-4 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }

  const own = tables.filter((table) => !table.system);
  const schema = place.tableId === SCHEMA;
  // the table the address names, else the first the agent keeps; none while it keeps none
  const open = schema ? null : tables.find((table) => table.id === place.tableId) ?? own[0] ?? null;
  const openTable = (tableId: string) => onPlace({ tableId, rowId: null });

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col md:flex-row">
      <TableChips tables={tables} open={open?.id ?? null} onOpen={openTable} onNewTable={() => setDialog({ kind: "new" })} />
      <SpacesRail
        agent={agent}
        tables={tables}
        open={open?.id ?? null}
        onOpen={openTable}
        onNewTable={() => setDialog({ kind: "new" })}
        onSettings={(table) => setDialog({ kind: "settings", table })}
        onAskDefinable={onAskDefinable && (() => onAskDefinable(`Look at ${agent.name}'s tables and tell me what you would change.`))}
      />
      {schema ? (
        <SchemaView agent={agent} tables={tables} onOpen={openTable} onView={(view) => view === "table" && onPlace({ tableId: null, rowId: null })} />
      ) : open ? (
        <TableView
          key={open.id}
          agent={agent}
          table={open}
          tables={tables}
          rowOpen={!!place.rowId}
          onOpenRow={(rowId) => onPlace({ tableId: open.id, rowId })}
          onNewRow={() => onPlace({ tableId: open.id, rowId: "new" })}
          onView={(view) => view === "schema" && onPlace({ tableId: SCHEMA, rowId: null })}
        />
      ) : (
        <StartPage
          agent={agent}
          onNewTable={() => setDialog({ kind: "new" })}
          onMade={() => onPlace({ tableId: null, rowId: null })}
          onAskDefinable={onAskDefinable}
        />
      )}
      {open && !open.system && place.rowId && (
        <RowPanel
          key={`${open.id}:${place.rowId}`}
          agentId={agent.id}
          table={open}
          tables={tables}
          rowId={place.rowId}
          onOpen={(tableId, rowId, how) => onPlace({ tableId, rowId }, how)}
          onOpenRun={onOpenRun}
          onClose={() => onPlace({ tableId: open.id, rowId: null })}
        />
      )}
      {dialog?.kind === "new" && (
        <NewTableDialog
          agentId={agent.id}
          onMade={(name) => {
            setDialog(null);
            setMade(name);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "settings" && (
        <TableSettingsDialog
          agentId={agent.id}
          table={dialog.table}
          onDropped={() => onPlace({ tableId: null, rowId: null })}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
